import React, { useCallback, useEffect, useRef, useState } from 'react';
import { lookupCanopyCases, createCanopyBatch, fetchCanopyBatch, fetchCanopySyncs, fetchTaxPrepSyncs } from '../../api';
import Modal from '../marketing/Modal';
import { toISODate } from '../marketing/shared';
import '../reports/Reports.css';
import '../marketing/Marketing.css';
import './Canopy.css';

// Canopy Sync: paste IRS Logics case numbers, check the cases the server
// pulled, and have them created in Canopy — or updated, when Canopy already
// has a client with that case number as its External ID.
//
// A run happens on the server in the background (one signed-in browser works
// through the cases), so this page starts it, then polls until every case has
// an outcome. Closing the tab doesn't stop the run; reopening the page shows
// it in the history below.

const POLL_EVERY_MS = 3000;

const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return `${toISODate(d)} ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

const formatPhone = (value) => {
    if (!value) return '';
    const d = String(value).replace(/\D/g, '');
    return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(value);
};

// The server sends "2006-01-02"; show it the way an EA reads a date, without
// the browser's timezone sliding it a day.
const formatBirthday = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return y && m && d ? `${m}/${d}/${y}` : iso;
};

// Case numbers come pasted from anywhere: one per line, comma-separated, or
// with other text around them. Anything that isn't a run of digits is ignored.
const parseCaseNumbers = (text) => {
    const seen = new Set();
    const out = [];
    for (const m of text.matchAll(/\d+/g)) {
        if (!seen.has(m[0])) {
            seen.add(m[0]);
            out.push(m[0]);
        }
    }
    return out;
};

const STATUS_LABEL = {
    queued: 'Waiting',
    running: 'In Canopy…',
    success: 'Done',
    duplicate: 'Name in use',
    failed: 'Failed',
};

const ACTION_LABEL = { create: 'Created', update: 'Updated', created: 'Created', updated: 'Updated' };

const TAXPREP_STATUS_LABEL = {
    queued: 'Waiting',
    running: 'Sending…',
    success: 'Done',
    failed: 'Failed',
};

// A Tax Prep row's detail: the link, and the fields Tax Prep kept as they
// were because someone had corrected them there (names only, never values).
function TaxPrepDetail({ sync }) {
    if (sync.status !== 'success') return sync.error || null;
    return (
        <>
            {sync.taxprep_client_url && (
                <a href={sync.taxprep_client_url} target="_blank" rel="noreferrer">Open in Tax Prep</a>
            )}
            {sync.conflicts && sync.conflicts.length > 0 && (
                <span className="canopy__warn-inline">
                    {' · '}Kept Tax Prep's own {sync.conflicts.join(', ')}
                </span>
            )}
        </>
    );
}

function TaxPrepPill({ status, action }) {
    const label = status === 'success' ? (ACTION_LABEL[action] || 'Done') : (TAXPREP_STATUS_LABEL[status] || status);
    return <span className={`canopy__pill canopy__pill--${status}`}>{label}</span>;
}

// The preview's last column: whether this client was synced before, and what
// Logics is missing for them.
function PreviewNote({ lastSync, taxPrepLastSync, hasSSN, hasBirthday }) {
    const missing = [!hasSSN && 'SSN', !hasBirthday && 'birthday'].filter(Boolean).join(' or ');
    return (
        <>
            {lastSync && (
                <span>
                    Synced before ({formatDateTime(lastSync.created_at)})
                    {lastSync.canopy_client_url && (
                        <> — <a href={lastSync.canopy_client_url} target="_blank" rel="noreferrer">open</a></>
                    )}
                </span>
            )}
            {taxPrepLastSync && (
                <span>
                    {lastSync ? ' · ' : ''}In Tax Prep since {formatDateTime(taxPrepLastSync.created_at)}
                    {taxPrepLastSync.taxprep_client_url && (
                        <> — <a href={taxPrepLastSync.taxprep_client_url} target="_blank" rel="noreferrer">open</a></>
                    )}
                </span>
            )}
            {missing && (
                <span className="canopy__warn-inline">
                    {lastSync ? ' · ' : ''}No {missing} in Logics
                </span>
            )}
        </>
    );
}

function StatusPill({ status, action }) {
    const label = status === 'success' ? (ACTION_LABEL[action] || 'Done') : (STATUS_LABEL[status] || status);
    return <span className={`canopy__pill canopy__pill--${status}`}>{label}</span>;
}

export default function CanopySync() {
    const [input, setInput] = useState('');
    // idle | looking | preview | starting | running | done
    const [phase, setPhase] = useState('idle');
    const [previews, setPreviews] = useState([]);
    const [error, setError] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [batch, setBatch] = useState(null);

    const [syncs, setSyncs] = useState([]);
    const [syncsError, setSyncsError] = useState(null);
    // Whether the server can push to Tax Prep (from the lookup), and whether
    // this batch should. Staff can untick it per batch.
    const [taxPrepEnabled, setTaxPrepEnabled] = useState(false);
    const [sendToTaxPrep, setSendToTaxPrep] = useState(true);
    const [taxPrepSyncs, setTaxPrepSyncs] = useState([]);
    const unmounted = useRef(false);
    useEffect(() => () => { unmounted.current = true; }, []);

    const loadSyncs = useCallback(async () => {
        try {
            setSyncs(await fetchCanopySyncs() || []);
            setSyncsError(null);
        } catch (err) {
            setSyncsError(err.message);
        }
        // The Tax Prep history is optional: a server without it answers an
        // empty list, and a failure here must not hide the Canopy history.
        try {
            setTaxPrepSyncs(await fetchTaxPrepSyncs() || []);
        } catch {
            setTaxPrepSyncs([]);
        }
    }, []);

    useEffect(() => { loadSyncs(); }, [loadSyncs]);

    const caseNumbers = parseCaseNumbers(input);
    const validPreviews = previews.filter((p) => !p.error);
    // A case with a spouse makes two Canopy clients.
    const clientCount = validPreviews.reduce((n, p) => n + (p.spouse ? 2 : 1), 0);
    const busy = phase === 'looking' || phase === 'starting' || phase === 'running';

    const reset = () => {
        setInput('');
        setPhase('idle');
        setPreviews([]);
        setError(null);
        setConfirming(false);
        setBatch(null);
    };

    const handleLookup = async (e) => {
        e.preventDefault();
        if (caseNumbers.length === 0) return;
        setError(null);
        setBatch(null);
        setPhase('looking');
        try {
            const res = await lookupCanopyCases(caseNumbers);
            setPreviews(res.cases || []);
            setTaxPrepEnabled(Boolean(res.taxprep_enabled));
            setPhase('preview');
        } catch (err) {
            setError(err.message);
            setPreviews([]);
            setPhase('idle');
        }
    };

    // Poll the batch until every case has an outcome. A failed poll (network
    // blip) just waits for the next tick; the server keeps going regardless.
    const watchBatch = useCallback(async (batchId) => {
        while (!unmounted.current) {
            await new Promise((r) => setTimeout(r, POLL_EVERY_MS));
            let res;
            try { res = await fetchCanopyBatch(batchId); } catch { continue; }
            if (unmounted.current) return;
            setBatch(res);
            if (res.done) {
                setPhase('done');
                loadSyncs();
                return;
            }
        }
    }, [loadSyncs]);

    const handleStart = async () => {
        setConfirming(false);
        setError(null);
        setPhase('starting');
        try {
            const res = await createCanopyBatch(validPreviews.map((p) => p.case_number), taxPrepEnabled && sendToTaxPrep);
            setBatch(res);
            setPhase('running');
            loadSyncs();
            watchBatch(res.batch_id);
        } catch (err) {
            setError(err.message);
            setPhase('preview');
        }
    };

    const counts = batch ? batch.syncs.reduce((acc, s) => {
        acc[s.status] = (acc[s.status] || 0) + 1;
        return acc;
    }, {}) : {};
    const finishedCount = batch ? batch.syncs.filter((s) => !['queued', 'running'].includes(s.status)).length : 0;
    const batchTaxPrep = (batch && batch.taxprep_syncs) || [];
    const taxPrepCounts = batchTaxPrep.reduce((acc, s) => {
        acc[s.status] = (acc[s.status] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="page">
            <div className="mkt__header">
                <div>
                    <h1 className="page__title">Canopy Sync</h1>
                    <p className="page__subtitle">
                        Paste one or more IRS Logics case numbers to pull the clients' details and create them in
                        Canopy. A case whose number is already a Canopy client's External ID is updated instead.
                        The SSN and birthday go in exactly as Logics has them.
                    </p>
                </div>
            </div>

            <form className="canopy__lookup" onSubmit={handleLookup}>
                <label className="mkt__field canopy__case-field">
                    <span>IRS Logics Case #s</span>
                    <textarea
                        className="canopy__textarea"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={'One per line, or comma-separated\ne.g. 12345, 12346'}
                        rows={3}
                        disabled={busy}
                        autoFocus
                    />
                    <span className="canopy__count">
                        {caseNumbers.length === 0 ? 'No case numbers yet' : `${caseNumbers.length} case number${caseNumbers.length === 1 ? '' : 's'}`}
                    </span>
                </label>
                <div className="canopy__lookup-actions">
                    <button type="submit" className="blueButton" disabled={busy || caseNumbers.length === 0}>
                        {phase === 'looking' ? 'Looking up…' : 'Look up'}
                    </button>
                    {(previews.length > 0 || error || batch) && (
                        <button type="button" className="mkt__btn-secondary" onClick={reset} disabled={busy}>
                            Clear
                        </button>
                    )}
                </div>
            </form>

            {error && <p className="mkt__error">{error}</p>}

            {previews.length > 0 && phase !== 'running' && phase !== 'done' && (
                <section className="mkt__panel canopy__preview">
                    <h2 className="mkt__panel-title">
                        {validPreviews.length} of {previews.length} case{previews.length === 1 ? '' : 's'} ready, {clientCount} client{clientCount === 1 ? '' : 's'} to sync
                    </h2>
                    <p className="mkt__panel-hint">
                        Check the details before syncing. On a married case, the spouse becomes a separate Canopy
                        client, with the case number plus “-S” as its External ID. Clients marked “synced before” will be
                        found in Canopy by their External ID and updated.
                    </p>

                    <div className="canopy__table-wrap">
                        <table className="canopy__table">
                            <thead>
                                <tr>
                                    <th>External ID</th>
                                    <th>Client name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Birthday</th>
                                    <th>SSN</th>
                                    <th>Address</th>
                                    <th>Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {previews.map((p) => (
                                    <React.Fragment key={p.case_number}>
                                        <tr className={p.error ? 'canopy__row--error' : ''}>
                                            <td>{p.case_number}</td>
                                            {p.error ? (
                                                <td colSpan={7} className="canopy__detail">{p.error}</td>
                                            ) : (
                                                <>
                                                    <td>{p.client_name}</td>
                                                    <td>{p.email || <em>none</em>}</td>
                                                    <td>{formatPhone(p.phone) || <em>none</em>}</td>
                                                    <td>{formatBirthday(p.birthday) || <em>none</em>}</td>
                                                    <td>{p.ssn_masked || <em>none</em>}</td>
                                                    <td className="canopy__detail">{p.address || <em>none</em>}</td>
                                                    <td className="canopy__detail">
                                                        <PreviewNote lastSync={p.last_sync} taxPrepLastSync={p.taxprep_last_sync} hasSSN={p.has_ssn} hasBirthday={p.has_birthday} />
                                                        {p.spouse_not_married && (
                                                            <span className="canopy__warn-inline">
                                                                {' · '}Spouse on the case not synced: marital status is {p.marital_status ? `“${p.marital_status}”` : 'blank'}
                                                            </span>
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                        {!p.error && p.spouse && (
                                            <tr className="canopy__row--spouse">
                                                <td>{p.spouse.external_id}</td>
                                                <td>
                                                    {p.spouse.client_name}
                                                    <span className="canopy__tag">Spouse</span>
                                                </td>
                                                <td>{p.spouse.email || <em>none</em>}</td>
                                                <td>{formatPhone(p.spouse.phone) || <em>none</em>}</td>
                                                <td>{formatBirthday(p.spouse.birthday) || <em>none</em>}</td>
                                                <td>{p.spouse.ssn_masked || <em>none</em>}</td>
                                                <td className="canopy__detail">
                                                    {p.spouse.address || <em>none</em>}
                                                    {p.spouse.address && !p.spouse.own_address && <em> (same as case)</em>}
                                                </td>
                                                <td className="canopy__detail">
                                                    <PreviewNote lastSync={p.spouse.last_sync} hasSSN={p.spouse.has_ssn} hasBirthday={p.spouse.has_birthday} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="canopy__actions">
                        <button
                            type="button"
                            className="blueButton"
                            onClick={() => setConfirming(true)}
                            disabled={busy || validPreviews.length === 0}
                        >
                            {phase === 'starting' ? 'Starting…' : `Sync ${clientCount} client${clientCount === 1 ? '' : 's'} to Canopy`}
                        </button>
                        {taxPrepEnabled && (
                            <label className="canopy__option">
                                <input
                                    type="checkbox"
                                    checked={sendToTaxPrep}
                                    onChange={(e) => setSendToTaxPrep(e.target.checked)}
                                    disabled={busy}
                                />
                                {' '}Also send {validPreviews.length === 1 ? 'this case' : `these ${validPreviews.length} cases`} to Tax Prep
                            </label>
                        )}
                    </div>
                </section>
            )}

            {batch && (phase === 'running' || phase === 'done') && (
                <section className="mkt__panel canopy__preview">
                    <h2 className="mkt__panel-title">
                        {phase === 'done' ? 'Run finished' : `Syncing… ${finishedCount} of ${batch.syncs.length} done`}
                    </h2>
                    {phase === 'running' && (
                        <p className="canopy__progress">
                            Canopy is being worked through in the background, about ten seconds per client. You can
                            leave this page; the outcome stays in the history below.
                        </p>
                    )}
                    {phase === 'done' && (
                        <p className={counts.failed || counts.duplicate ? 'mkt__preview mkt__preview--warn' : 'mkt__success'}>
                            {counts.success || 0} succeeded
                            {counts.duplicate ? `, ${counts.duplicate} with a name already in use` : ''}
                            {counts.failed ? `, ${counts.failed} failed` : ''}.
                        </p>
                    )}
                    <div className="canopy__table-wrap">
                        <table className="canopy__table">
                            <thead>
                                <tr>
                                    <th>External ID</th>
                                    <th>Client</th>
                                    <th>Status</th>
                                    <th>Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {batch.syncs.map((s) => (
                                    <tr key={s.id}>
                                        <td>{s.case_number}</td>
                                        <td>{s.client_name}</td>
                                        <td><StatusPill status={s.status} action={s.action} /></td>
                                        <td className="canopy__detail">
                                            {s.status === 'success' && s.canopy_client_url && (
                                                <a href={s.canopy_client_url} target="_blank" rel="noreferrer">Open in Canopy</a>
                                            )}
                                            {s.status !== 'success' && s.error}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {batchTaxPrep.length > 0 && (
                        <>
                            <h3 className="mkt__panel-title canopy__subtitle">Tax Prep</h3>
                            {phase === 'done' && (
                                <p className={taxPrepCounts.failed ? 'mkt__preview mkt__preview--warn' : 'mkt__success'}>
                                    {taxPrepCounts.success || 0} sent to Tax Prep
                                    {taxPrepCounts.failed ? `, ${taxPrepCounts.failed} failed` : ''}.
                                </p>
                            )}
                            <div className="canopy__table-wrap">
                                <table className="canopy__table">
                                    <thead>
                                        <tr>
                                            <th>Case #</th>
                                            <th>Client</th>
                                            <th>Status</th>
                                            <th>Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batchTaxPrep.map((s) => (
                                            <tr key={`tp-${s.id}`}>
                                                <td>{s.case_number}</td>
                                                <td>{s.client_name}</td>
                                                <td><TaxPrepPill status={s.status} action={s.action} /></td>
                                                <td className="canopy__detail"><TaxPrepDetail sync={s} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                    {phase === 'done' && (
                        <div className="canopy__actions">
                            <button type="button" className="blueButton" onClick={reset}>Start another</button>
                        </div>
                    )}
                </section>
            )}

            {confirming && (
                <Modal
                    title="Sync to Canopy?"
                    onClose={() => setConfirming(false)}
                    onSubmit={handleStart}
                    submitLabel={`Sync ${clientCount}`}
                    submitting={phase === 'starting'}
                >
                    <p className="mkt-modal__text">
                        This creates <strong>{clientCount}</strong> client{clientCount === 1 ? '' : 's'} in
                        Canopy, updating any that already exist there with the same External ID, including the
                        SSN and birthday from Logics. Spouses are created as their own clients.
                    </p>
                    {taxPrepEnabled && sendToTaxPrep && (
                        <p className="mkt-modal__text">
                            Each case also goes to <strong>Tax Prep</strong> as one client with the spouse attached, so a
                            return can be prepared there. A case already in Tax Prep is updated; details a preparer has
                            corrected there are kept.
                        </p>
                    )}
                    <p className="mkt-modal__text">
                        It runs in the background at about ten seconds per client.
                    </p>
                </Modal>
            )}

            <section className="canopy__history">
                <h2 className="mkt__panel-title">Recent syncs</h2>
                {syncsError && <p className="mkt__error">{syncsError}</p>}
                {!syncsError && syncs.length === 0 && (
                    <p className="page__subtitle">No clients have been synced from here yet.</p>
                )}
                {syncs.length > 0 && (
                    <div className="canopy__table-wrap">
                        <table className="canopy__table">
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>External ID</th>
                                    <th>Client</th>
                                    <th>By</th>
                                    <th>Status</th>
                                    <th>Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {syncs.map((s) => (
                                    <tr key={s.id}>
                                        <td>{formatDateTime(s.created_at)}</td>
                                        <td>{s.case_number}</td>
                                        <td>{s.client_name}</td>
                                        <td>{s.requested_by}</td>
                                        <td><StatusPill status={s.status} action={s.action} /></td>
                                        <td className="canopy__detail">
                                            {s.status === 'success' && s.canopy_client_url && (
                                                <a href={s.canopy_client_url} target="_blank" rel="noreferrer">Open in Canopy</a>
                                            )}
                                            {s.status !== 'success' && s.error}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {taxPrepSyncs.length > 0 && (
                    <>
                        <h3 className="mkt__panel-title canopy__subtitle">Recent Tax Prep pushes</h3>
                        <div className="canopy__table-wrap">
                            <table className="canopy__table">
                                <thead>
                                    <tr>
                                        <th>When</th>
                                        <th>Case #</th>
                                        <th>Client</th>
                                        <th>By</th>
                                        <th>Status</th>
                                        <th>Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {taxPrepSyncs.map((s) => (
                                        <tr key={`tph-${s.id}`}>
                                            <td>{formatDateTime(s.created_at)}</td>
                                            <td>{s.case_number}</td>
                                            <td>{s.client_name}</td>
                                            <td>{s.requested_by}</td>
                                            <td><TaxPrepPill status={s.status} action={s.action} /></td>
                                            <td className="canopy__detail"><TaxPrepDetail sync={s} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
}
